/**
 * PATH: packages/email-hub/src/feature/lib/savedSearches.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-SAVEDSEARCHES-001
 * PURPOSE: localStorage-only saved-search store for the inbox search
 *          bar (G8 slice ? no backend). Pure I?O load/save/remove
 *          helpers over an org-scoped key.
 * WHY: Operators re-type the same operator-laden queries daily
 *      ("from:simpro has:attachment"); this persists them per org.
 *      LANDMINE: key is org-scoped, NOT user-scoped ? two users on
 *      one machine/profile share the list. Acceptable for the local
 *      slice; a backend-synced version would replace this store.
 * RELATED:
 *  - components/SavedSearchesMenu.tsx: the UI consuming this store.
 */

export interface SavedSearch {
  id: string
  name: string
  query: string
  created_at: string
}

/** Hard cap ? the dropdown is a quick-pick list, not a database. */
export const SAVED_SEARCHES_MAX = 20

export function savedSearchesKey(orgId: number | null | undefined): string {
  return `qt-email-saved-searches:${orgId ?? '0'}`
}

/** f(storage, orgId) ? the saved list (newest first). Corrupt or
 *  missing storage yields []. */
export function loadSavedSearches(orgId: number | null | undefined): SavedSearch[] {
  try {
    const raw = window.localStorage.getItem(savedSearchesKey(orgId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (s): s is SavedSearch =>
        !!s &&
        typeof (s as SavedSearch).id === 'string' &&
        typeof (s as SavedSearch).name === 'string' &&
        typeof (s as SavedSearch).query === 'string',
    )
  } catch {
    return []
  }
}

function persist(orgId: number | null | undefined, list: SavedSearch[]): SavedSearch[] {
  try {
    window.localStorage.setItem(savedSearchesKey(orgId), JSON.stringify(list))
  } catch {
    /* storage full/disabled ? list is ephemeral for this tab */
  }
  return list
}

/** f(orgId, name, query) ? updated list with the new entry prepended.
 *  A duplicate query (exact string match) replaces the old entry
 *  instead of stacking a twin. List is capped at SAVED_SEARCHES_MAX. */
export function saveSearch(
  orgId: number | null | undefined,
  name: string,
  query: string,
): SavedSearch[] {
  const trimmedName = name.trim()
  const trimmedQuery = query.trim()
  if (!trimmedName || !trimmedQuery) return loadSavedSearches(orgId)
  const entry: SavedSearch = {
    id: `ss-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    name: trimmedName,
    query: trimmedQuery,
    created_at: new Date().toISOString(),
  }
  const rest = loadSavedSearches(orgId).filter(s => s.query !== trimmedQuery)
  return persist(orgId, [entry, ...rest].slice(0, SAVED_SEARCHES_MAX))
}

/** f(orgId, id) ? updated list with the entry removed. */
export function deleteSavedSearch(
  orgId: number | null | undefined,
  id: string,
): SavedSearch[] {
  return persist(orgId, loadSavedSearches(orgId).filter(s => s.id !== id))
}
